import * as React from 'react';

import { useId } from '@coursera/cds-core';

const SIZE_MAP = {
  small: { width: 24, height: 24 },
  medium: { width: 36, height: 36 },
  large: { width: 48, height: 48 },
} as const;

type Props = {
  size?: keyof typeof SIZE_MAP;
  colorVariant?: 'default' | 'inverted';
};

const WESAskCourseraIcon = ({ size = 'large', colorVariant = 'default' }: Props) => {
  const { width, height } = SIZE_MAP[size];
  const uniqueId = useId();
  const linearGradientId = `wes-ask-coursera-linear-${uniqueId}`;
  const clipPathId = `wes-ask-coursera-clip-${uniqueId}`;

  // New gradient IDs for the inverted state sparkles
  const sparkleGradient1Id = `wes-ask-coursera-sparkle1-${uniqueId}`;
  const sparkleGradient2Id = `wes-ask-coursera-sparkle2-${uniqueId}`;

  const isInverted = colorVariant === 'inverted';

  return (
    <svg
      data-testid="WESAskCourseraIcon-svg"
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 48 48"
      fill="none"
    >
      {/* Background Circle */}
      <path
        data-testid="WESAskCourseraIcon-bg"
        d="M0 23.999C0 10.7447 10.7447 0 23.999 0C37.2533 0 47.998 10.7447 47.998 23.999C47.998 37.2533 37.2533 47.998 23.999 47.998C10.7447 47.998 0 37.2533 0 23.999Z"
        fill={isInverted ? '#F0F6FF' : `url(#${linearGradientId})`}
      />

      {/* Sparkles */}
      <g clipPath={`url(#${clipPathId})`}>
        <path
          data-testid="WESAskCourseraIcon-sparkle1"
          d="M31.7493 13.1795C31.598 12.8089 31.0731 12.8089 30.9218 13.1795L30.2766 14.7599C30.1253 15.1305 29.8312 15.4246 29.4606 15.5759L27.8802 16.2212C27.5096 16.3725 27.5096 16.8973 27.8802 17.0486L29.4606 17.6938C29.8312 17.8452 30.1253 18.1393 30.2766 18.5099L30.9218 20.0903C31.0731 20.4609 31.598 20.4609 31.7493 20.0903L32.3945 18.5099C32.5458 18.1393 32.8399 17.8452 33.2105 17.6938L34.7909 17.0486C35.1615 16.8973 35.1615 16.3725 34.7909 16.2212L33.2105 15.5759C32.8399 15.4246 32.5458 15.1305 32.3945 14.7599L31.7493 13.1795Z"
          fill={isInverted ? `url(#${sparkleGradient1Id})` : 'white'}
        />
        <path
          data-testid="WESAskCourseraIcon-sparkle2"
          d="M23.3147 15.8541C23.0623 15.2358 22.1868 15.2358 21.9343 15.8541L20.5666 19.2041C19.8093 21.0589 18.3374 22.5308 16.4827 23.288L13.1326 24.6558C12.5144 24.9082 12.5144 25.7837 13.1326 26.0361L16.4827 27.4039C18.3374 28.1611 19.8093 29.6331 20.5666 31.4878L21.9343 34.8378C22.1868 35.4561 23.0623 35.4561 23.3147 34.8378L24.6824 31.4878C25.4397 29.6331 26.9116 28.1611 28.7664 27.4039L32.1164 26.0361C32.7346 25.7837 32.7346 24.9082 32.1164 24.6558L28.7664 23.288C26.9116 22.5308 25.4397 21.0589 24.6824 19.2041L23.3147 15.8541Z"
          fill={isInverted ? `url(#${sparkleGradient2Id})` : 'white'}
        />
      </g>

      <defs>
        {/* Default background gradient */}
        <linearGradient
          data-testid="WESAskCourseraIcon-linear-bg"
          id={linearGradientId}
          x1="-3.63143"
          y1="47.998"
          x2="61.3384"
          y2="46.3899"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#0048B0" />
          <stop offset="1" stopColor="#1E72EB" />
        </linearGradient>

        {/* Inverted sparkle gradients */}
        <linearGradient
          id={sparkleGradient1Id}
          x1="10.9742"
          y1="35.3016"
          x2="41.2946"
          y2="34.5511"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#0048B0" />
          <stop offset="1" stopColor="#1E72EB" />
        </linearGradient>
        <linearGradient
          id={sparkleGradient2Id}
          x1="10.9742"
          y1="35.3016"
          x2="41.2946"
          y2="34.5511"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#0048B0" />
          <stop offset="1" stopColor="#1E72EB" />
        </linearGradient>

        <clipPath id={clipPathId}>
          <rect width="24.502" height="24.502" fill="white" transform="translate(11.748 11.748)" />
        </clipPath>
      </defs>
    </svg>
  );
};

export default WESAskCourseraIcon;
